import { Popover } from '@sgme/ui';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import { useToggle } from 'hooks/useToggle';
import type { IntlKey } from 'locales';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { MoreDetailsButton } from './MoreDetailsButtons';
import { StatusMessage } from './StatusMessage';
import { StatusPanel } from './StatusPanel';

type TechnicalErrorProps = Readonly<{
  id: string;
  value: ApiErrorMessage;
}>;

type DetailsPopoverProps = {
  msgText: string;
};

const DetailsPopover: FC<DetailsPopoverProps> = ({ msgText }) => {
  const { isOn, toggle } = useToggle(false);

  return (
    <Popover
      style={{ padding: 0 }}
      maxWidth={300}
      open={isOn}
      contents={
        <div style={{ minWidth: 200 }} className="bg-lvl2 text-primary p-2 shadow border border-danger">
          <i className="icon icon-sm clickable float-end me-2" onClick={toggle} data-e2e="error-details-close-button">
            close
          </i>
          <div className="p-4 text-start">
            <span>{msgText}</span>
          </div>
        </div>
      }
    >
      <div>
        <MoreDetailsButton onClick={toggle} />
      </div>
    </Popover>
  );
};

export const TechnicalError: FC<TechnicalErrorProps> = ({ value: { message, values } }) => {
  const { formatMessage } = useIntl();

  const detailledMessage = useMemo(
    () => formatMessage({ id: message as IntlKey }, values),
    [formatMessage, message, values],
  );

  if (message === undefined) {
    return null;
  }

  const hasTimeout = message.toLowerCase().includes('timeout');
  const msgKey: IntlKey = hasTimeout ? ('dashboard.failed.timeout' as const) : ('dashboard.failed' as const);

  return (
    <StatusPanel className="alert-danger">
      <StatusMessage icon={hasTimeout ? 'history_toggle_off' : 'error'} msgKey={msgKey} />
      <DetailsPopover msgText={detailledMessage} />
    </StatusPanel>
  );
};

TechnicalError.displayName = 'TechnicalError';
